import type { Camada, FigurinhaDaBiblioteca } from "@foto/shared";

/**
 * O que a tela sabe de cada figurinha que uma camada cita.
 *
 * A camada guarda só o `figurinhaId`: o arquivo, o tamanho e a URL (assinada, que vence) vêm da biblioteca.
 * A figurinha pode **deixar de existir** — o dono a apagou, ou ela deixou de ser pública — e aí o servidor
 * recusa o pedido da folha; a tela precisa saber disso antes, para avisar e deixar remover a camada.
 *
 * `indisponivel` é só o "não encontrada" do servidor. Falha de rede é `erro`: não se afirma que a imagem
 * sumiu quando só não se conseguiu perguntar.
 */
export type EstadoDaFigurinha =
  | { estado: "ok"; figurinha: FigurinhaDaBiblioteca }
  | { estado: "carregando" }
  | { estado: "indisponivel" }
  | { estado: "erro"; mensagem: string };

export type FigurinhasDasCamadas = ReadonlyMap<number, EstadoDaFigurinha>;

/** Os ids das figurinhas que as camadas citam, sem repetir, em ordem crescente (chave estável para a consulta). */
export function idsDasFigurinhas(camadas: readonly Camada[]): number[] {
  const ids = new Set<number>();
  for (const c of camadas) if (c.tipo === "imagem") ids.add(c.figurinhaId);
  return [...ids].sort((a, b) => a - b);
}

/** A figurinha, quando a tela já a tem. */
export function figurinhaDe(
  mapa: FigurinhasDasCamadas | undefined,
  id: number,
): FigurinhaDaBiblioteca | undefined {
  const e = mapa?.get(id);
  return e?.estado === "ok" ? e.figurinha : undefined;
}

/** As camadas de imagem cuja figurinha não existe mais (ou não é mais visível para quem pede). */
export function camadasIndisponiveis(
  camadas: readonly Camada[],
  mapa: FigurinhasDasCamadas | undefined,
): Camada[] {
  return camadas.filter(
    (c) => c.tipo === "imagem" && mapa?.get(c.figurinhaId)?.estado === "indisponivel",
  );
}

/** O que o hook lê de uma consulta do tRPC. */
export type ConsultaDeFigurinha = {
  data?: FigurinhaDaBiblioteca | undefined;
  isError: boolean;
  error: { message: string; data?: { code?: string } | null } | null;
};

const naoEncontrada = (c: ConsultaDeFigurinha): boolean =>
  c.isError && c.error?.data?.code === "NOT_FOUND";

/**
 * O que uma consulta diz da figurinha. "Não encontrada" **vence os dados que já havia**: a resposta da escolha no
 * seletor fica guardada, e se a figurinha é apagada depois, a próxima consulta falha com "não encontrada" — o
 * dado velho continua lá, e o servidor, ainda assim, recusaria o pedido. Já uma falha de rede com dado em mãos
 * não condena nada: a imagem segue como estava.
 */
export function estadoDaConsulta(c: ConsultaDeFigurinha): EstadoDaFigurinha {
  if (naoEncontrada(c)) return { estado: "indisponivel" };
  if (c.data) return { estado: "ok", figurinha: c.data };
  if (c.isError)
    return { estado: "erro", mensagem: c.error?.message ?? "Não foi possível consultar a imagem" };
  return { estado: "carregando" };
}

export const resumoDoEstado = (e: EstadoDaFigurinha): string =>
  e.estado === "ok"
    ? `ok:${e.figurinha.id}:${e.figurinha.urls.tela}`
    : e.estado === "erro"
      ? `erro:${e.mensagem}`
      : e.estado;
